import { useTranslation } from 'react-i18next';

import { ToolCallCard } from './ToolCallCard.js';
import type { ChatMessage as ChatMessageType, ContentBlock } from './types.js';

interface ChatMessageProps {
  message: ChatMessageType;
  /** Tool results, keyed by tool_use_id, for assistant messages whose tool_use
      blocks haven't yet had a paired tool message inserted next to them. */
  resultsByUseId?: Map<string, { result: unknown; isError?: boolean; durationMs?: number }>;
}

function isToolResultBlock(b: ContentBlock): b is Extract<ContentBlock, { type: 'tool_result' }> {
  return b.type === 'tool_result';
}

export function ChatMessage({ message }: ChatMessageProps): JSX.Element {
  const { t } = useTranslation('ai');
  const isUser = message.role === 'user';
  const isTool = message.role === 'tool';

  // The "tool" pseudo-role wraps tool_result blocks rendered alongside the
  // upstream assistant message.
  if (isTool) {
    return (
      <div className="space-y-1">
        {message.content.filter(isToolResultBlock).map((b) => {
          let parsed: unknown = b.content;
          try { parsed = JSON.parse(b.content); } catch { /* keep raw */ }
          return (
            <ToolCallCard
              key={b.toolUseId}
              name={t('tool.resultLabel')}
              input={{}}
              result={parsed}
              {...(b.isError ? { isError: true } : {})}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className={['flex', isUser ? 'justify-end' : 'justify-start'].join(' ')}>
      <div
        className={[
          'max-w-[85%] rounded-2xl px-3 py-2 text-sm',
          isUser
            ? 'bg-brand text-brand-contrast'
            : 'bg-bg-muted text-fg',
        ].join(' ')}
      >
        <div className="space-y-2">
          {message.content.map((b, i) => {
            if (b.type === 'text') {
              return <p key={i} className="whitespace-pre-wrap break-words">{b.text}</p>;
            }
            if (b.type === 'tool_use') {
              return <ToolCallCard key={`${b.id}-${i}`} name={b.name} input={b.input} />;
            }
            return null;
          })}
        </div>
      </div>
    </div>
  );
}
